import { useEffect, useMemo, useState } from 'react';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';

type MDEditorType = typeof import('@uiw/react-md-editor').default;

interface MarkdownEditorProps {
  id?: string;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: number;
  className?: string;
}

export default function MarkdownEditor({
  id,
  name,
  value,
  onChange,
  placeholder,
  height = 220,
  className,
}: MarkdownEditorProps) {
  const [MDEditor, setMDEditor] = useState<MDEditorType | null>(null);

  useEffect(() => {
    // Dynamic import avoids SSR crashes because the editor touches browser-only APIs.
    let mounted = true;

    import('@uiw/react-md-editor').then(module => {
      if (mounted) {
        setMDEditor(() => module.default);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  const editor = useMemo(() => {
    if (!MDEditor) return null;

    return (
      <MDEditor
        value={value}
        onChange={nextValue => onChange(nextValue ?? '')}
        preview="live"
        height={height}
        textareaProps={{ id, name, placeholder }}
      />
    );
  }, [MDEditor, height, id, name, onChange, placeholder, value]);

  if (!editor) {
    return (
      <textarea
        id={id}
        name={name}
        className={className}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={6}
      />
    );
  }

  return (
    <div
      data-color-mode={
        document.documentElement.getAttribute('data-theme') || 'light'
      }
    >
      {editor}
    </div>
  );
}
